import axios from 'axios';

// ======================== Configuration ========================
const BASE_URL = 'https://loyalty.bom.asia';

const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Interceptors
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// ======================== User Quiz Functions ========================

/**
 * Lấy danh sách users với pagination và filters
 * @param {Object} params - Query parameters
 * @param {number} params.page - Trang hiện tại (default: 1)
 * @param {number} params.limit - Số items mỗi trang (default: 10)
 * @param {string} params.role_code - Filter theo role_code (optional)
 * @param {boolean} params.followedOA - Filter theo followedOA (optional)
 * @param {string} params.quizName - Filter theo tên quiz (optional, tìm users đã làm quiz có tên khớp)
 */
export const fetchUsers = async (params = {}) => {
  try {
    const { page = 1, limit = 10, role_code, followedOA, quizName } = params;
    
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });

    if (role_code) queryParams.append('role_code', role_code);
    if (followedOA !== undefined) queryParams.append('followedOA', followedOA.toString());
    if (quizName) queryParams.append('quizName', quizName);

    const response = await apiClient.get(`/api/miniApp/quiz/users?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to fetch users' };
  }
};

/**
 * Lấy thông tin chi tiết user theo userId
 * @param {string} userId - ID của user
 */
export const fetchUserByUserId = async (userId) => {
  try {
    const response = await apiClient.get(`/api/miniApp/quiz/users/${userId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to fetch user' };
  }
};

/**
 * Xóa user quiz theo userId
 * @param {string} userId - ID của user cần xóa
 * @returns {Promise<Object>} Response data từ server
 * 
 * @example
 * // Xóa user có userId = '3369156244687554492'
 * await deleteUserByUserId('3369156244687554492');
 */
export const deleteUserByUserId = async (userId) => {
  try {
    if (!userId) {
      throw { message: 'userId is required' };
    }
    
    const response = await apiClient.delete(`/api/miniApp/quiz/users/${userId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to delete user' };
  }
};

/**
 * Lấy danh sách users theo quizId
 * @param {number} quizId - ID của quiz
 * @param {Object} params - Query parameters (optional)
 * @param {number} params.page - Trang hiện tại (default: 1)
 * @param {number} params.limit - Số items mỗi trang (default: 10)
 * @returns {Promise<Object>} Response data với danh sách users
 * 
 * @example
 * // Lấy users đã làm quiz có ID = 12
 * await fetchUsersByQuiz(12);
 * 
 * @example
 * // Lấy users với pagination
 * await fetchUsersByQuiz(12, { page: 1, limit: 20 });
 */
export const fetchUsersByQuiz = async (quizId, params = {}) => {
  try {
    if (!quizId) {
      throw { message: 'quizId is required' };
    }
    
    const { page = 1, limit = 10 } = params;
    
    const queryParams = new URLSearchParams({
      quizId: quizId.toString(),
      page: page.toString(),
      limit: limit.toString()
    });
    
    const response = await apiClient.get(`/api/miniApp/quiz/users/by-quiz?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to fetch users by quiz' };
  }
};

/**
 * Helper function - Lấy tất cả users (all pages)
 * @param {Object} filters - Filter options
 */
export const fetchAllUsers = async (filters = {}) => {
  try {
    let allUsers = [];
    let currentPage = 1;
    let hasMore = true;

    while (hasMore) {
      const response = await fetchUsers({ 
        ...filters, 
        page: currentPage, 
        limit: 100 // Fetch 100 items per page for efficiency
      });

      if (response.success && response.data) {
        allUsers = [...allUsers, ...response.data.users];
        
        const { page, totalPages } = response.data.pagination;
        hasMore = page < totalPages;
        currentPage++;
      } else {
        hasMore = false;
      }
    }

    return {
      success: true,
      data: {
        users: allUsers,
        total: allUsers.length
      }
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Helper function - Filter users locally
 * @param {Array} users - Array of users
 * @param {Object} filters - Filter criteria
 */
export const filterUsersLocally = (users, filters) => {
  let filtered = [...users];

  if (filters.role_code) {
    filtered = filtered.filter(user => user.role_code === filters.role_code);
  }

  if (filters.followedOA !== undefined) {
    filtered = filtered.filter(user => user.followedOA === filters.followedOA);
  }

  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter(user => 
      user.userId?.toLowerCase().includes(searchLower) ||
      user.name?.toLowerCase().includes(searchLower) ||
      user.phone?.toLowerCase().includes(searchLower)
    );
  }

  return filtered;
};

/**
 * Helper function - Sort users
 * @param {Array} users - Array of users
 * @param {string} sortBy - Field to sort by
 * @param {string} sortOrder - 'asc' or 'desc'
 */
export const sortUsers = (users, sortBy = 'createdAt', sortOrder = 'desc') => {
  return [...users].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];

    // Handle dates
    if (sortBy === 'createdAt' || sortBy === 'updatedAt') {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    }

    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });
};

/**
 * Helper function - Download file from blob
 * @param {Blob} blob - Blob object
 * @param {string} filename - Filename for download
 */
const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Export users to Excel file
 * @param {Object} params - Query parameters
 * @param {boolean} params.includeAttempts - Include attempts data (default: false)
 * @param {number} params.quizId - Filter by quizId (optional)
 * @param {string} params.role_code - Filter by role_code (optional)
 * @param {number} params.minPoints - Filter by minimum points (optional)
 * @param {string} params.filename - Custom filename (optional, default: auto-generated)
 * @returns {Promise<void>}
 * 
 * @example
 * // Export all users with attempts
 * await exportUsers({ includeAttempts: true });
 * 
 * @example
 * // Export users who did quiz 12 with attempts
 * await exportUsers({ quizId: 12, includeAttempts: true });
 * 
 * @example
 * // Export with multiple filters
 * await exportUsers({ 
 *   quizId: 12, 
 *   role_code: 'R1', 
 *   includeAttempts: true, 
 *   minPoints: 50 
 * });
 */
export const exportUsers = async (params = {}) => {
  try {
    const { 
      includeAttempts = false, 
      quizId, 
      role_code, 
      minPoints,
      filename 
    } = params;
    
    const queryParams = new URLSearchParams();
    
    if (includeAttempts) queryParams.append('includeAttempts', 'true');
    if (quizId) queryParams.append('quizId', quizId.toString());
    if (role_code) queryParams.append('role_code', role_code);
    if (minPoints !== undefined) queryParams.append('minPoints', minPoints.toString());

    // Use axios with responseType: 'blob' for file download
    const response = await apiClient.get(`/api/miniApp/quiz/users/export?${queryParams.toString()}`, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    });

    // Validate response before downloading
    const contentType = response.headers['content-type'] || response.headers['Content-Type'] || '';
    const blob = response.data;

    // Check response status first
    if (response.status < 200 || response.status >= 300) {
      // Clone blob to read error message
      const clonedBlob = blob.slice();
      try {
        const text = await clonedBlob.text();
        // Try to parse as JSON error
        if (text.trim().startsWith('{') || text.trim().startsWith('[')) {
          const errorData = JSON.parse(text);
          throw errorData || { 
            message: `Server returned error (Status: ${response.status})`,
            status: response.status
          };
        }
        // HTML error page
        throw { 
          message: `Server returned error (Status: ${response.status}). ${text.substring(0, 200)}...`,
          status: response.status,
          html: text.substring(0, 500) // First 500 chars for debugging
        };
      } catch (parseError) {
        throw { 
          message: `Server returned error (Status: ${response.status})`,
          status: response.status
        };
      }
    }

    // Check if response is an error (JSON/HTML) instead of Excel file
    if (contentType.includes('application/json') || 
        contentType.includes('text/html') ||
        contentType.includes('text/plain')) {
      // Clone blob to read error message
      const clonedBlob = blob.slice();
      try {
        const text = await clonedBlob.text();
        // Try to parse as JSON error
        if (text.trim().startsWith('{') || text.trim().startsWith('[')) {
          const errorData = JSON.parse(text);
          throw errorData || { message: 'Server returned error instead of Excel file' };
        }
        // HTML error page - extract useful info
        const titleMatch = text.match(/<title>(.*?)<\/title>/i);
        const title = titleMatch ? titleMatch[1] : 'Unknown error';
        const bodyMatch = text.match(/<body[^>]*>(.*?)<\/body>/is);
        const bodyText = bodyMatch ? bodyMatch[1].replace(/<[^>]+>/g, ' ').substring(0, 200) : text.substring(0, 200);
        
        throw { 
          message: `Server returned HTML instead of Excel file. ${title}. ${bodyText}...`,
          status: response.status,
          contentType: contentType,
          htmlPreview: text.substring(0, 500) // First 500 chars for debugging
        };
      } catch (parseError) {
        // If it's already our error object, re-throw it
        if (parseError.message && parseError.status) {
          throw parseError;
        }
        throw { 
          message: `Server returned invalid response. Expected Excel file but got: ${contentType}`,
          status: response.status,
          contentType: contentType
        };
      }
    }

    // Validate Excel file magic bytes FIRST (before reading text which consumes blob)
    // Excel files (.xlsx) are ZIP archives, they start with "PK" (0x50 0x4B)
    const firstBytes = await blob.slice(0, 4).arrayBuffer();
    const uint8Array = new Uint8Array(firstBytes);
    const magicBytes = String.fromCharCode(...uint8Array);
    
    // Validate blob size (Excel files are typically > 1KB)
    if (blob.size < 1000) {
      // Clone blob before reading text (to avoid consuming original)
      const clonedBlob = blob.slice();
      try {
        const text = await clonedBlob.text();
        // Check if it's a JSON error
        if (text.trim().startsWith('{') || text.trim().startsWith('[')) {
          const errorData = JSON.parse(text);
          throw errorData || { message: 'Response too small, likely an error message' };
        }
        // Check if it's HTML error
        if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
          throw { message: 'Server returned HTML error page instead of Excel file' };
        }
        throw { message: `Response too small (${blob.size} bytes) to be a valid Excel file` };
      } catch (parseError) {
        if (parseError.message && parseError.message.includes('Server returned')) {
          throw parseError;
        }
        throw { message: `Response too small (${blob.size} bytes) to be a valid Excel file` };
      }
    }
    
    // Check magic bytes - Excel files must start with "PK"
    if (magicBytes.substring(0, 2) !== 'PK') {
      // Not a valid Excel/ZIP file - clone blob to read error message
      const clonedBlob = blob.slice();
      try {
        const text = await clonedBlob.text();
        if (text.trim().startsWith('{') || text.trim().startsWith('[')) {
          const errorData = JSON.parse(text);
          throw errorData || { message: 'Server returned JSON error instead of Excel file' };
        }
        if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
          throw { message: 'Server returned HTML error page instead of Excel file' };
        }
        throw { message: 'File is not a valid Excel file (missing ZIP signature "PK")' };
      } catch (parseError) {
        if (parseError.message && !parseError.message.includes('Server returned')) {
          throw { message: 'File is not a valid Excel file (missing ZIP signature "PK")' };
        }
        throw parseError;
      }
    }

    // Generate filename if not provided
    let exportFilename = filename;
    if (!exportFilename) {
      const timestamp = new Date().toISOString().split('T')[0];
      const parts = [];
      if (quizId) parts.push(`quiz${quizId}`);
      if (role_code) parts.push(role_code);
      if (minPoints) parts.push(`min${minPoints}pts`);
      const suffix = parts.length > 0 ? `_${parts.join('_')}` : '';
      exportFilename = `users${suffix}_${timestamp}.xlsx`;
    }

    // Create a new blob with correct MIME type to ensure Excel recognizes it
    const excelBlob = new Blob([blob], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });

    // Download the file
    downloadFile(excelBlob, exportFilename);
    
    return { success: true, filename: exportFilename };
  } catch (error) {
    
    // Try to parse error message from blob response
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const errorData = JSON.parse(text);
        throw errorData || { message: 'Failed to export users' };
      } catch (parseError) {
        throw { message: 'Failed to export users' };
      }
    }
    
    throw error.response?.data || { message: 'Failed to export users' };
  }
};

// ======================== Checkin Users Functions ========================

/**
 * Lấy danh sách users đã đăng ký hoặc checkin event
 * @param {Object} params - Query parameters
 * @param {number} params.eventId - ID của event (required)
 * @param {number} params.page - Trang hiện tại (default: 1)
 * @param {number} params.limit - Số items mỗi trang (default: 10)
 * @param {string} params.filterType - Filter type: 'checkin' để lấy users đã checkin, không có để lấy users đã đăng ký (optional)
 * @param {string} params.role_code - Filter theo role_code (optional)
 * @returns {Promise<Object>} Response data với users và pagination
 * 
 * @example
 * // Lấy users đã đăng ký event
 * await fetchCheckinUsers({ eventId: 1, page: 1, limit: 10 });
 * 
 * @example
 * // Lấy users đã checkin event
 * await fetchCheckinUsers({ eventId: 1, filterType: 'checkin', page: 1, limit: 10 });
 */
export const fetchCheckinUsers = async (params = {}) => {
  try {
    const { eventId, page = 1, limit = 10, filterType, role_code } = params;
    
    if (!eventId) {
      throw { message: 'eventId is required' };
    }

    const queryParams = new URLSearchParams({
      eventId: eventId.toString(),
      page: page.toString(),
      limit: limit.toString()
    });

    if (filterType) queryParams.append('filterType', filterType);
    if (role_code) queryParams.append('role_code', role_code);

    const response = await apiClient.get(`/api/miniApp/checkin/users?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to fetch checkin users' };
  }
};

/**
 * Export checkin users to Excel file
 * @param {Object} params - Query parameters
 * @param {number} params.eventId - ID của event (required)
 * @param {string} params.filterType - Filter type: 'checkin' để export users đã checkin (optional)
 * @param {boolean} params.includeRegistrations - Include registration data (default: false)
 * @param {boolean} params.includeCheckins - Include checkin data (default: false)
 * @param {string} params.role_code - Filter by role_code (optional)
 * @param {string} params.filename - Custom filename (optional, default: auto-generated)
 * @returns {Promise<Object>} { success: true, filename: string }
 * 
 * @example
 * // Export users đã đăng ký event
 * await exportCheckinUsers({ 
 *   eventId: 1, 
 *   includeRegistrations: true, 
 *   includeCheckins: true 
 * });
 * 
 * @example
 * // Export users đã checkin event
 * await exportCheckinUsers({ 
 *   eventId: 1, 
 *   filterType: 'checkin', 
 *   includeCheckins: true 
 * });
 * 
 * @example
 * // Export với nhiều filters
 * await exportCheckinUsers({ 
 *   eventId: 1, 
 *   role_code: 'R1', 
 *   includeRegistrations: true, 
 *   includeCheckins: true 
 * });
 */
export const exportCheckinUsers = async (params = {}) => {
  try {
    const { 
      eventId,
      filterType,
      includeRegistrations = false, 
      includeCheckins = false, 
      role_code,
      filename 
    } = params;
    
    const queryParams = new URLSearchParams();
    
    // eventId is optional - if not provided, export all users
    if (eventId) {
      queryParams.append('eventId', eventId.toString());
    }
    
    if (filterType) queryParams.append('filterType', filterType);
    if (includeRegistrations) queryParams.append('includeRegistrations', 'true');
    if (includeCheckins) queryParams.append('includeCheckins', 'true');
    if (role_code) queryParams.append('role_code', role_code);

    // Use axios with responseType: 'blob' for file download
    const response = await apiClient.get(`/api/miniApp/checkin/users/export?${queryParams.toString()}`, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    });

    // Validate response before downloading
    const contentType = response.headers['content-type'] || response.headers['Content-Type'] || '';
    const blob = response.data;

    // Check response status first
    if (response.status < 200 || response.status >= 300) {
      const clonedBlob = blob.slice();
      try {
        const text = await clonedBlob.text();
        if (text.trim().startsWith('{') || text.trim().startsWith('[')) {
          const errorData = JSON.parse(text);
          throw errorData || { 
            message: `Server returned error (Status: ${response.status})`,
            status: response.status
          };
        }
        throw { 
          message: `Server returned error (Status: ${response.status}). ${text.substring(0, 200)}...`,
          status: response.status,
          html: text.substring(0, 500)
        };
      } catch (parseError) {
        throw { 
          message: `Server returned error (Status: ${response.status})`,
          status: response.status
        };
      }
    }

    // Check if response is an error (JSON/HTML) instead of Excel file
    if (contentType.includes('application/json') || 
        contentType.includes('text/html') ||
        contentType.includes('text/plain')) {
      const clonedBlob = blob.slice();
      try {
        const text = await clonedBlob.text();
        if (text.trim().startsWith('{') || text.trim().startsWith('[')) {
          const errorData = JSON.parse(text);
          throw errorData || { message: 'Server returned error instead of Excel file' };
        }
        const titleMatch = text.match(/<title>(.*?)<\/title>/i);
        const title = titleMatch ? titleMatch[1] : 'Unknown error';
        const bodyMatch = text.match(/<body[^>]*>(.*?)<\/body>/is);
        const bodyText = bodyMatch ? bodyMatch[1].replace(/<[^>]+>/g, ' ').substring(0, 200) : text.substring(0, 200);
        
        throw { 
          message: `Server returned HTML instead of Excel file. ${title}. ${bodyText}...`,
          status: response.status,
          contentType: contentType,
          htmlPreview: text.substring(0, 500)
        };
      } catch (parseError) {
        if (parseError.message && parseError.status) {
          throw parseError;
        }
        throw { 
          message: `Server returned invalid response. Expected Excel file but got: ${contentType}`,
          status: response.status,
          contentType: contentType
        };
      }
    }

    // Validate Excel file magic bytes FIRST (before reading text which consumes blob)
    const firstBytes = await blob.slice(0, 4).arrayBuffer();
    const uint8Array = new Uint8Array(firstBytes);
    const magicBytes = String.fromCharCode(...uint8Array);
    
    // Validate blob size (Excel files are typically > 1KB)
    if (blob.size < 1000) {
      const clonedBlob = blob.slice();
      try {
        const text = await clonedBlob.text();
        if (text.trim().startsWith('{') || text.trim().startsWith('[')) {
          const errorData = JSON.parse(text);
          throw errorData || { message: 'Response too small, likely an error message' };
        }
        if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
          throw { message: 'Server returned HTML error page instead of Excel file' };
        }
        throw { message: `Response too small (${blob.size} bytes) to be a valid Excel file` };
      } catch (parseError) {
        if (parseError.message && parseError.message.includes('Server returned')) {
          throw parseError;
        }
        throw { message: `Response too small (${blob.size} bytes) to be a valid Excel file` };
      }
    }
    
    // Check magic bytes - Excel files must start with "PK"
    if (magicBytes.substring(0, 2) !== 'PK') {
      const clonedBlob = blob.slice();
      try {
        const text = await clonedBlob.text();
        if (text.trim().startsWith('{') || text.trim().startsWith('[')) {
          const errorData = JSON.parse(text);
          throw errorData || { message: 'Server returned JSON error instead of Excel file' };
        }
        if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
          throw { message: 'Server returned HTML error page instead of Excel file' };
        }
        throw { message: 'File is not a valid Excel file (missing ZIP signature "PK")' };
      } catch (parseError) {
        if (parseError.message && !parseError.message.includes('Server returned')) {
          throw { message: 'File is not a valid Excel file (missing ZIP signature "PK")' };
        }
        throw parseError;
      }
    }

    // Generate filename if not provided
    let exportFilename = filename;
    if (!exportFilename) {
      const timestamp = new Date().toISOString().split('T')[0];
      const parts = [];
      if (eventId) {
        parts.push(`event${eventId}`);
      } else {
        parts.push('all_events');
      }
      if (filterType) parts.push(filterType);
      if (role_code) parts.push(role_code);
      const suffix = parts.length > 0 ? `_${parts.join('_')}` : '';
      exportFilename = `checkin_users${suffix}_${timestamp}.xlsx`;
    }

    // Create a new blob with correct MIME type to ensure Excel recognizes it
    const excelBlob = new Blob([blob], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });

    // Download the file
    downloadFile(excelBlob, exportFilename);
    
    return { success: true, filename: exportFilename };
  } catch (error) {
    
    // Try to parse error message from blob response
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const errorData = JSON.parse(text);
        throw errorData || { message: 'Failed to export checkin users' };
      } catch (parseError) {
        throw { message: 'Failed to export checkin users' };
      }
    }
    
    throw error.response?.data || { message: 'Failed to export checkin users' };
  }
};

// ======================== Default Export ========================
export default {
  fetchUsers,
  fetchUserByUserId,
  deleteUserByUserId,
  fetchUsersByQuiz,
  fetchAllUsers,
  filterUsersLocally,
  sortUsers,
  exportUsers,
  fetchCheckinUsers,
  exportCheckinUsers,
};

